// Background music: a quiet, endless noir lounge-jazz loop composed in code (walking bass, electric piano,
// brushed drums and an occasional vibraphone line). Nothing is recorded or copied: the chords are a
// common minor-key progression and every note is chosen on the fly, so it never repeats exactly.
import { audioContext, musicDestination, whenUnlocked } from './sound.ts';
import { getSettings, onSettings } from './settings.ts';

const BPM = 86;
const BEAT = 60 / BPM;
const BAR = BEAT * 4;
const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

interface Chord { root: number; tones: number[]; voicing: number[]; scale: number[] }
// tones = bass chord tones (semitones above root); voicing = piano notes (absolute midi); scale = melody notes (absolute midi)
const C = (root: number, tones: number[], voicing: number[], scale: number[]): Chord => ({ root, tones, voicing, scale });
const A_MINOR = [69, 72, 74, 76, 79, 81];
const SECTION_A: Chord[] = [
  C(45, [0, 3, 7, 10], [60, 64, 67, 71], A_MINOR), // Am9
  C(45, [0, 3, 7, 10], [60, 64, 67, 69], A_MINOR),
  C(50, [0, 3, 7, 10], [60, 65, 69, 72], [69, 72, 74, 77, 81]), // Dm7
  C(50, [0, 3, 7, 10], [60, 64, 65, 69], [69, 72, 74, 77, 81]),
  C(47, [0, 3, 6, 10], [57, 62, 65, 69], [69, 71, 74, 77]), // Bm7b5
  C(40, [0, 4, 7, 10], [56, 62, 65, 68], [68, 71, 74, 76]), // E7b9
  C(45, [0, 3, 7, 10], [60, 64, 67, 71], A_MINOR),
  C(40, [0, 4, 7, 10], [56, 62, 64, 68], [68, 71, 74, 76]), // E7
];
const SECTION_B: Chord[] = [
  C(41, [0, 4, 7, 11], [57, 60, 64, 67], [69, 72, 76, 79]), // Fmaj7
  C(41, [0, 4, 7, 11], [57, 60, 64, 69], [69, 72, 76, 79]),
  C(40, [0, 3, 7, 10], [55, 59, 62, 67], [67, 71, 74, 76]), // Em7
  C(45, [0, 4, 7, 10], [55, 61, 64, 67], [69, 73, 76, 79]), // A7
  C(50, [0, 3, 7, 10], [60, 65, 69, 72], [69, 72, 74, 77]), // Dm7
  C(43, [0, 4, 7, 10], [59, 62, 65, 69], [71, 74, 77, 79]), // G7
  C(48, [0, 4, 7, 11], [59, 64, 67, 71], [72, 76, 79, 81]), // Cmaj7
  C(40, [0, 4, 7, 10], [56, 62, 64, 68], [68, 71, 74, 76]), // E7
];
const FORM = [...SECTION_A, ...SECTION_A, ...SECTION_B, ...SECTION_A];

let out: GainNode | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let nextBarAt = 0;
let bar = 0;
let noise: AudioBuffer | null = null;

const pick = <T,>(xs: T[]): T => xs[Math.floor(Math.random() * xs.length)];
const swing = (eighth: number) => Math.floor(eighth / 2) * BEAT + (eighth % 2 ? BEAT * 0.66 : 0);

function noiseBuffer(ac: AudioContext): AudioBuffer {
  if (noise) return noise;
  noise = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
  const d = noise.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return noise;
}

function gainEnv(ac: AudioContext, t: number, peak: number, attack: number, decay: number): GainNode {
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  return g;
}

function bass(ac: AudioContext, t: number, note: number, len: number) {
  const f = midi(note);
  const o = ac.createOscillator();
  o.type = 'triangle';
  o.frequency.value = f;
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(900, t);
  lp.frequency.exponentialRampToValueAtTime(260, t + 0.25);
  const g = gainEnv(ac, t, 0.32, 0.008, len * 1.1);
  o.connect(lp).connect(g).connect(out!);
  o.start(t);
  o.stop(t + len * 1.2 + 0.1);
  const s = ac.createOscillator();
  s.frequency.value = f;
  const gs = gainEnv(ac, t, 0.18, 0.01, len);
  s.connect(gs).connect(out!);
  s.start(t);
  s.stop(t + len + 0.1);
}

/** Electric piano: a sine "tine" with a quickly fading FM bite. */
function epiano(ac: AudioContext, t: number, note: number, len: number, vel: number) {
  const f = midi(note);
  const car = ac.createOscillator();
  car.frequency.value = f;
  const mod = ac.createOscillator();
  mod.frequency.value = f * 2;
  const idx = ac.createGain();
  idx.gain.setValueAtTime(f * 1.2, t);
  idx.gain.exponentialRampToValueAtTime(f * 0.05, t + 0.4);
  mod.connect(idx).connect(car.frequency);
  const g = gainEnv(ac, t, 0.045 * vel, 0.006, len);
  car.connect(g).connect(out!);
  car.start(t);
  mod.start(t);
  car.stop(t + len + 0.1);
  mod.stop(t + len + 0.1);
}

function vibes(ac: AudioContext, t: number, note: number, len: number) {
  const f = midi(note);
  const o = ac.createOscillator();
  o.frequency.value = f;
  const o2 = ac.createOscillator();
  o2.frequency.value = f * 4;
  const trem = ac.createGain();
  trem.gain.value = 0.8;
  const lfo = ac.createOscillator();
  const depth = ac.createGain();
  lfo.frequency.value = 5.5;
  depth.gain.value = 0.2;
  lfo.connect(depth).connect(trem.gain);
  const g = gainEnv(ac, t, 0.07, 0.004, len);
  const g2 = gainEnv(ac, t, 0.012, 0.002, 0.25);
  o.connect(trem).connect(g).connect(out!);
  o2.connect(g2).connect(out!);
  for (const x of [o, o2, lfo]) { x.start(t); x.stop(t + len + 0.1); }
}

function brush(ac: AudioContext, t: number, peak: number, len: number, freq: number) {
  const src = ac.createBufferSource();
  src.buffer = noiseBuffer(ac);
  const f = ac.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = freq;
  f.Q.value = 0.6;
  const g = gainEnv(ac, t, peak, 0.02, len);
  src.connect(f).connect(g).connect(out!);
  src.start(t, Math.random() * 0.5);
  src.stop(t + len + 0.1);
}

function ride(ac: AudioContext, t: number, peak: number) {
  const src = ac.createBufferSource();
  src.buffer = noiseBuffer(ac);
  const f = ac.createBiquadFilter();
  f.type = 'highpass';
  f.frequency.value = 7000;
  const g = gainEnv(ac, t, peak, 0.002, 0.35);
  src.connect(f).connect(g).connect(out!);
  src.start(t, Math.random() * 0.5);
  src.stop(t + 0.45);
  const ping = ac.createOscillator();
  ping.type = 'square';
  ping.frequency.value = 5100;
  const gp = gainEnv(ac, t, peak * 0.08, 0.001, 0.15);
  ping.connect(gp).connect(out!);
  ping.start(t);
  ping.stop(t + 0.2);
}

function kick(ac: AudioContext, t: number) {
  const o = ac.createOscillator();
  o.frequency.setValueAtTime(70, t);
  o.frequency.exponentialRampToValueAtTime(40, t + 0.15);
  const g = gainEnv(ac, t, 0.12, 0.004, 0.2);
  o.connect(g).connect(out!);
  o.start(t);
  o.stop(t + 0.3);
}

const COMP_PATTERNS: [number, number][][] = [
  // [eighth index, velocity]
  [[3, 1], [6, 0.8]],
  [[1, 0.9], [4, 0.7], [7, 0.8]],
  [[0, 1], [5, 0.8]],
  [[3, 0.9], [7, 0.7]],
  [[2, 1], [5, 0.7], [7, 0.6]],
];

function scheduleBar(ac: AudioContext, t: number, n: number) {
  const chord = FORM[n % FORM.length];
  const next = FORM[(n + 1) % FORM.length];
  const intro = n < 2;

  // Walking bass: root, two chord tones, then a step into the next chord.
  const tones = chord.tones.map((x) => chord.root + x);
  const line = [chord.root, pick(tones.slice(1)), pick(tones.slice(1)), next.root + (Math.random() < 0.5 ? 1 : -1)];
  if (!intro) line.forEach((note, i) => bass(ac, t + i * BEAT, note, BEAT * 0.95));
  else bass(ac, t, chord.root, BAR * 0.9);

  // Piano comping on swung off-beats.
  const pattern = intro ? [[0, 0.8] as [number, number]] : pick(COMP_PATTERNS);
  for (const [e, vel] of pattern) {
    const len = e === 0 ? BAR * 0.9 : BEAT * 1.4;
    chord.voicing.forEach((note, i) => epiano(ac, t + swing(e) + i * 0.012, note, len, vel));
  }

  // Brushes on 2 and 4, ride "ding, ding-a ding".
  if (!intro) {
    for (const b of [1, 3]) brush(ac, t + b * BEAT, 0.05, 0.25, 2500);
    for (let b = 0; b < 4; b++) brush(ac, t + b * BEAT, 0.015, BEAT * 0.9, 1200); // the circular stir
    for (const e of [0, 2, 3, 4, 6, 7]) ride(ac, t + swing(e), e % 2 ? 0.02 : 0.035);
    for (const b of [0, 2]) kick(ac, t + b * BEAT);
  }

  // Vibraphone: a short phrase now and then, chord tones on the strong beats.
  if (!intro && (n % 4 === 0 || n % 4 === 2) && Math.random() < 0.55) {
    let e = pick([0, 1, 2]);
    let last = pick(chord.scale);
    while (e < 8) {
      const strong = e % 2 === 0;
      const choices = chord.scale.filter((x) => Math.abs(x - last) <= 5);
      const note = strong ? pick(chord.voicing.map((v) => v + 12).filter((x) => x >= 66 && x <= 84).concat(last)) : pick(choices.length ? choices : chord.scale);
      vibes(ac, t + swing(e), note, BEAT * 1.6);
      last = note;
      e += pick([1, 1, 2, 2, 3]);
    }
  }
}

function tick() {
  const ac = audioContext();
  if (!ac || !out || ac.state !== 'running') return;
  if (nextBarAt < ac.currentTime) nextBarAt = ac.currentTime + 0.1;
  while (nextBarAt < ac.currentTime + 0.8) {
    scheduleBar(ac, nextBarAt, bar++);
    nextBarAt += BAR;
  }
}

export function startMusic() {
  const ac = audioContext();
  const dest = musicDestination();
  if (!ac || !dest || timer) return;
  out = ac.createGain();
  out.gain.setValueAtTime(0, ac.currentTime);
  out.gain.linearRampToValueAtTime(1, ac.currentTime + 2);
  out.connect(dest);
  bar = 0;
  nextBarAt = ac.currentTime + 0.2;
  tick();
  timer = setInterval(tick, 200);
}

export function stopMusic() {
  const ac = audioContext();
  if (timer) clearInterval(timer);
  timer = null;
  if (ac && out) {
    const old = out;
    old.gain.setTargetAtTime(0.0001, ac.currentTime, 0.4);
    setTimeout(() => old.disconnect(), 2500);
  }
  out = null;
}

export function musicPlaying(): boolean {
  return timer !== null;
}

/** Start the music after the first click if it's switched on, and follow the on/off option. */
export function installMusic() {
  whenUnlocked(() => {
    if (getSettings().music) startMusic();
  });
  onSettings((s) => {
    if (s.music && !musicPlaying()) whenUnlocked(startMusic);
    if (!s.music && musicPlaying()) stopMusic();
  });
}

/** For the automated sound check: write `bars` bars of music into any audio context. */
export function renderBarsForTest(ac: BaseAudioContext, dest: AudioNode, bars: number) {
  out = (ac as AudioContext).createGain();
  out.connect(dest);
  for (let n = 0; n < bars; n++) scheduleBar(ac as AudioContext, 0.1 + n * BAR, n);
}
